import { CheckCircle2, Info, TriangleAlert, X } from 'lucide-react';
import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router';

type Tone = 'success' | 'info' | 'error';

interface Toast {
  id: number;
  tone: Tone;
  title: string;
  body?: string;
  href?: string;
}

type ToastInput = Omit<Toast, 'id'>;

const ToastContext = createContext<(toast: ToastInput) => void>(() => undefined);

export const useToast = () => useContext(ToastContext);

const ICONS = { success: CheckCircle2, info: Info, error: TriangleAlert };
const TONES = { success: 'text-brand', info: 'text-info', error: 'text-danger' };

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(1);

  const dismiss = useCallback((id: number) => setToasts((all) => all.filter((t) => t.id !== id)), []);
  const push = useCallback(
    (input: ToastInput) => {
      const id = nextId.current++;
      setToasts((all) => [...all.slice(-2), { ...input, id }]);
      setTimeout(() => dismiss(id), input.tone === 'error' ? 8000 : 5000);
    },
    [dismiss],
  );
  const value = useMemo(() => push, [push]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        aria-live="polite"
        className="pointer-events-none fixed inset-x-0 top-3 z-50 flex flex-col items-center gap-2 px-4 sm:top-auto sm:right-4 sm:bottom-4 sm:left-auto sm:items-end"
      >
        {toasts.map((t) => {
          const Icon = ICONS[t.tone];
          const content = (
            <>
              <Icon className={`mt-0.5 size-5 shrink-0 ${TONES[t.tone]}`} aria-hidden />
              <div className="min-w-0 flex-1">
                <p className="font-medium">{t.title}</p>
                {t.body && <p className="mt-0.5 line-clamp-2 text-sm text-muted">{t.body}</p>}
              </div>
            </>
          );
          return (
            <div
              key={t.id}
              role={t.tone === 'error' ? 'alert' : 'status'}
              className="pointer-events-auto flex w-full max-w-sm items-start gap-3 rounded-xl border border-line bg-surface p-3.5 shadow-lg"
            >
              {t.href ? (
                <Link to={t.href} className="flex min-w-0 flex-1 gap-3" onClick={() => dismiss(t.id)}>
                  {content}
                </Link>
              ) : (
                content
              )}
              <button
                type="button"
                onClick={() => dismiss(t.id)}
                className="-m-1 rounded-md p-1 text-muted hover:text-text"
                aria-label="Dismiss"
              >
                <X className="size-4" />
              </button>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}
